// frontend/modules/drafts/components/DraftProcessList.tsx
import React from 'react';
import { Table, Button, Space, Tag, Tooltip, Modal } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ExclamationCircleOutlined, 
  DeleteOutlined,
  EyeOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { DraftProcess, DraftProcessStatus } from '../types';

const { confirm } = Modal;

interface DraftProcessListProps {
  draftProcesses: DraftProcess[];
  onView: (draft: DraftProcess) => void;
  onResume: (draft: DraftProcess) => void;
  onDelete: (id: string) => void;
  onCancel: (id: string) => void;
}

const DraftProcessList: React.FC<DraftProcessListProps> = ({
  draftProcesses,
  onView,
  onResume,
  onDelete,
  onCancel,
}) => {
  const { dark } = useTheme();

  const formatRelativeTime = (date: string) => {
    const target = new Date(date).getTime();
    const now = Date.now();
    const diffMs = now - target;

    if (Number.isNaN(target) || diffMs < 0) {
      return 'just now';
    }

    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;

    if (diffMs < minute) {
      return 'just now';
    }

    if (diffMs < hour) {
      const minutes = Math.floor(diffMs / minute);
      return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
    }

    if (diffMs < day) {
      const hours = Math.floor(diffMs / hour);
      return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    }

    const days = Math.floor(diffMs / day);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  };

  const statusTag = (status: string) => {
    switch (status) {
      case DraftProcessStatus.PENDING:
        return <Tag color="processing">Pending</Tag>;
      case DraftProcessStatus.COMPLETED:
        return <Tag color="success">Completed</Tag>;
      case DraftProcessStatus.CANCELED:
        return <Tag color="default">Canceled</Tag>;
      case DraftProcessStatus.FAILED:
        return <Tag color="error">Failed</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const handleDelete = (id: string) => {
    confirm({
      title: 'Are you sure you want to delete this draft process?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes, delete it',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        onDelete(id);
      },
    });
  };

  const handleCancel = (id: string) => {
    confirm({
      title: 'Are you sure you want to cancel this draft process?',
      icon: <ExclamationCircleOutlined />,
      content: 'This will mark the process as canceled and it cannot be resumed.',
      okText: 'Yes, cancel it',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        onCancel(id);
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: DraftProcess, b: DraftProcess) => a.name.localeCompare(b.name),
      render: (text: string) => <span className={dark ? 'text-white' : ''}>{text}</span>,
    },
    {
      title: 'Process Type',
      dataIndex: 'processType',
      key: 'processType',
      sorter: (a: DraftProcess, b: DraftProcess) => a.processType.localeCompare(b.processType),
      render: (text: string) => <span className={dark ? 'text-white' : ''}>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (a: DraftProcess, b: DraftProcess) => a.status.localeCompare(b.status),
      render: (status: string) => statusTag(status),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: DraftProcess, b: DraftProcess) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: string) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <span className={dark ? 'text-white' : ''}>
            {formatRelativeTime(date)}
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_:any, record: DraftProcess) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => onView(record)}
              type="text"
              size="small"
            />
          </Tooltip>
          
          {record.status === DraftProcessStatus.PENDING && (
            <>
              <Tooltip title="Resume Process">
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => onResume(record)}
                  type="text"
                  size="small"
                  className="text-green-600"
                />
              </Tooltip>
              
              <Tooltip title="Cancel Process">
                <Button
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleCancel(record.id)}
                  type="text"
                  size="small"
                  danger
                />
              </Tooltip>
            </>
          )}
          
          <Tooltip title="Delete Process">
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
              type="text"
              size="small"
              danger
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={draftProcesses}
      columns={columns}
      rowKey="id"
      pagination={{ pageSize: 10 }}
      className={dark ? 'bg-gray-800 text-white' : ''}
    />
  );
};

export default DraftProcessList;
